import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart,
  LogOut,
  Lock,
  Loader2,
  Save,
  Plus,
  Trash2,
  Inbox,
  Mail,
  BarChart3,
  ImageIcon,
  Settings as SettingsIcon,
  BookOpen,
  CheckCircle2,
  Clock,
  ArrowLeft,
} from "lucide-react";
import {
  adminApi,
  adminLogin,
  adminMe,
  clearToken,
  getToken,
  API,
} from "../lib/adminApi";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";

/* -------------------------------------------------------------- */
/*  Login                                                           */
/* -------------------------------------------------------------- */
function LoginForm({ onLoggedIn }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      await adminLogin(pw);
      toast.success("Welcome back");
      onLoggedIn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F7F4] px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl bg-white border border-[#E0DCD3] p-8 md:p-10 shadow-sm"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#D85C41] text-white">
            <Lock className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <div>
            <p className="text-xs uppercase tracking-[0.16em] font-bold text-[#D85C41]">
              CCDKOFHAMILTON
            </p>
            <h1 className="font-display text-2xl font-bold">Admin sign in</h1>
          </div>
        </div>
        <p className="mt-5 text-sm text-[#57534E]">
          Enter the admin password to manage stats, stories, site info, and
          incoming requests.
        </p>
        <input
          data-testid="admin-password-input"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Admin password"
          className="mt-6 w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1C1917] px-7 py-3 text-sm font-semibold text-white hover:bg-[#D85C41] transition-all disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Signing in…
            </>
          ) : (
            "Sign in"
          )}
        </button>
        <a
          href="/"
          className="mt-4 block text-center text-xs text-[#78716C] hover:text-[#D85C41]"
        >
          ← Back to website
        </a>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Stats Tab                                                       */
/* -------------------------------------------------------------- */
function StatsTab({ site, onSave }) {
  const [stats, setStats] = useState(site.stats);
  const [saving, setSaving] = useState(false);

  useEffect(() => setStats(site.stats), [site.stats]);

  const update = (k, v) =>
    setStats((s) => ({ ...s, [k]: Number.isNaN(parseInt(v, 10)) ? 0 : parseInt(v, 10) }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ ...site, stats });
      toast.success("Stats updated");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { k: "loads_washed", label: "Loads Washed" },
    { k: "children_supported", label: "Children Supported" },
    { k: "families_helped", label: "Families Helped" },
    { k: "volunteer_hours", label: "Volunteer Hours" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold">Impact stats</h2>
        <p className="text-sm text-[#57534E] mt-1">
          Edit the four numbers shown on the homepage. Set to 0 to display "—".
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.k}>
            <label className="block text-sm font-semibold mb-2">{f.label}</label>
            <input
              data-testid={`stat-input-${f.k}`}
              type="number"
              min="0"
              value={stats[f.k] ?? 0}
              onChange={(e) => update(f.k, e.target.value)}
              className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
            />
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="stats-save-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#D85C41] px-6 py-3 text-sm font-semibold text-white hover:bg-[#BD4C33] disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save stats
      </button>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Stories Tab                                                     */
/* -------------------------------------------------------------- */
function StoriesTab({ site, onSave }) {
  const [stories, setStories] = useState(site.stories || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => setStories(site.stories || []), [site.stories]);

  const addStory = () => {
    setStories((s) => [
      ...s,
      { id: crypto.randomUUID(), name: "", quote: "", image_url: "" },
    ]);
  };

  const remove = (id) => setStories((s) => s.filter((x) => x.id !== id));
  const updateStory = (id, k, v) =>
    setStories((s) => s.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ ...site, stories });
      toast.success("Stories saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold">Stories</h2>
          <p className="text-sm text-[#57534E] mt-1 max-w-xl">
            Add real stories from families, children, or volunteers. Paste a
            photo URL — or leave it blank for a placeholder. Below 1 story shows
            blank placeholders on the public site.
          </p>
        </div>
        <button
          onClick={addStory}
          data-testid="add-story-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#D85C41]"
        >
          <Plus className="h-4 w-4" /> Add story
        </button>
      </div>

      {stories.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#D6D3D1] bg-[#F0EFEB] p-8 text-center text-sm text-[#78716C]">
          No stories yet — placeholders show on the homepage. Click "Add story"
          to start.
        </div>
      )}

      <div className="space-y-5">
        {stories.map((s, i) => (
          <div
            key={s.id}
            data-testid={`story-row-${i}`}
            className="rounded-2xl border border-[#E0DCD3] bg-white p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4"
          >
            <div className="md:col-span-3">
              {s.image_url ? (
                <img
                  src={s.image_url}
                  alt=""
                  className="w-full h-32 md:h-full object-cover rounded-xl"
                  onError={(e) => (e.currentTarget.style.opacity = 0.3)}
                />
              ) : (
                <div className="w-full h-32 md:h-full rounded-xl bg-[#EAE5DF] flex flex-col items-center justify-center text-[#A8A29E] text-xs">
                  <ImageIcon className="h-6 w-6 mb-1" />
                  No photo
                </div>
              )}
            </div>
            <div className="md:col-span-9 grid grid-cols-1 gap-3">
              <input
                placeholder="Name (e.g. Maya, age 9 — or 'A Hamilton volunteer')"
                value={s.name}
                onChange={(e) => updateStory(s.id, "name", e.target.value)}
                className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40"
              />
              <textarea
                placeholder="Their quote or story (no quotes needed — we'll add them)"
                rows={3}
                value={s.quote}
                onChange={(e) => updateStory(s.id, "quote", e.target.value)}
                className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 resize-y"
              />
              <input
                placeholder="Photo URL (optional) — paste from Imgur, Cloudinary, etc."
                value={s.image_url}
                onChange={(e) => updateStory(s.id, "image_url", e.target.value)}
                className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40"
              />
              <div className="flex justify-end">
                <button
                  onClick={() => remove(s.id)}
                  data-testid={`remove-story-${i}`}
                  className="inline-flex items-center gap-1.5 text-sm text-[#BD4C33] hover:text-[#D85C41]"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="stories-save-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#D85C41] px-6 py-3 text-sm font-semibold text-white hover:bg-[#BD4C33] disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save stories
      </button>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Hero Photos Tab                                                 */
/* -------------------------------------------------------------- */
function PhotosTab({ site, onSave }) {
  const [imgs, setImgs] = useState(site.hero_images || ["", "", ""]);
  const [saving, setSaving] = useState(false);

  useEffect(() => setImgs(site.hero_images || ["", "", ""]), [site.hero_images]);

  const update = (i, v) =>
    setImgs((arr) => arr.map((x, j) => (i === j ? v : x)));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ ...site, hero_images: imgs });
      toast.success("Photos saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold">Hero photos</h2>
        <p className="text-sm text-[#57534E] mt-1 max-w-2xl">
          Three photos in the homepage hero collage. Paste a public image URL
          for each (e.g. from Imgur, Cloudinary, Google Drive shared link, or
          an Unsplash photo URL). Leave blank to show a placeholder box.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {imgs.map((url, i) => (
          <div key={i} className="rounded-2xl border border-[#E0DCD3] bg-white p-4">
            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[#EAE5DF] mb-3 flex items-center justify-center text-[#A8A29E]">
              {url ? (
                <img src={url} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="text-center text-xs">
                  <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                  Photo {i + 1}
                </div>
              )}
            </div>
            <input
              placeholder={`Photo ${i + 1} URL`}
              value={url}
              onChange={(e) => update(i, e.target.value)}
              data-testid={`hero-img-input-${i}`}
              className="w-full rounded-xl border border-[#D6D3D1] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40"
            />
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="photos-save-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#D85C41] px-6 py-3 text-sm font-semibold text-white hover:bg-[#BD4C33] disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save photos
      </button>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Site Info Tab                                                   */
/* -------------------------------------------------------------- */
function SiteInfoTab({ site, onSave }) {
  const [c, setC] = useState(site.contact);
  const [saving, setSaving] = useState(false);

  useEffect(() => setC(site.contact), [site.contact]);

  const update = (k, v) => setC((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ ...site, contact: c });
      toast.success("Site info saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { k: "email", label: "Email", placeholder: "you@example.com" },
    { k: "phone", label: "Phone", placeholder: "(513) 555-1234" },
    { k: "address", label: "Address / Area", placeholder: "Hamilton, Ohio" },
    { k: "hours", label: "Hours", placeholder: "Tue – Sat • 9am – 6pm" },
    { k: "donate_url", label: "Donate URL (Venmo, PayPal, etc.)", placeholder: "https://venmo.com/u/..." },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold">Site info</h2>
        <p className="text-sm text-[#57534E] mt-1">
          Public contact info that appears in the navbar, footer, and contact
          section.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.k} className={f.k === "donate_url" ? "md:col-span-2" : ""}>
            <label className="block text-sm font-semibold mb-2">{f.label}</label>
            <input
              data-testid={`siteinfo-input-${f.k}`}
              value={c[f.k] || ""}
              placeholder={f.placeholder}
              onChange={(e) => update(f.k, e.target.value)}
              className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
            />
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="siteinfo-save-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#D85C41] px-6 py-3 text-sm font-semibold text-white hover:bg-[#BD4C33] disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save info
      </button>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Help Requests Tab                                               */
/* -------------------------------------------------------------- */
const STATUS_LABELS = {
  new: { label: "New", color: "bg-[#D85C41] text-white" },
  scheduled: { label: "Scheduled", color: "bg-[#EBB04D] text-[#1C1917]" },
  completed: { label: "Completed", color: "bg-[#9EA992] text-white" },
  cancelled: { label: "Cancelled", color: "bg-[#A8A29E] text-white" },
};

function HelpRequestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/help-requests");
      setItems(data);
    } catch (e) {
      toast.error("Couldn't load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/help-requests/${id}`, { status });
      setItems((arr) =>
        arr.map((x) => (x.id === id ? { ...x, status } : x)),
      );
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request? This can't be undone.")) return;
    try {
      await adminApi.delete(`/admin/help-requests/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (loading)
    return (
      <div className="flex items-center gap-2 text-sm text-[#78716C]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Help requests</h2>
        <span className="text-sm text-[#78716C]">{items.length} total</span>
      </div>

      {items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#D6D3D1] bg-[#F0EFEB] p-10 text-center text-sm text-[#78716C]">
          No requests yet. When families submit the form, they'll show up here.
        </div>
      )}

      {items.map((r, i) => {
        const s = STATUS_LABELS[r.status] || STATUS_LABELS.new;
        return (
          <div
            key={r.id}
            data-testid={`help-request-${i}`}
            className="rounded-2xl border border-[#E0DCD3] bg-white p-5 md:p-6"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display text-lg font-bold">{r.full_name}</h3>
                <p className="text-sm text-[#57534E]">
                  {r.contact_method}: <span className="font-semibold">{r.contact_value}</span>
                </p>
                <p className="text-xs text-[#78716C] mt-1">
                  {new Date(r.created_at).toLocaleString()} • {r.num_children}{" "}
                  child{r.num_children === 1 ? "" : "ren"}
                  {r.location_area ? ` • ${r.location_area}` : ""}
                  {r.preferred_date ? ` • ${r.preferred_date}` : ""}
                </p>
              </div>
              <span className={`text-xs font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full ${s.color}`}>
                {s.label}
              </span>
            </div>

            {r.notes && (
              <p className="mt-3 text-sm text-[#57534E] bg-[#F0EFEB] rounded-xl p-3 whitespace-pre-wrap">
                {r.notes}
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {r.status !== "scheduled" && (
                <button
                  onClick={() => updateStatus(r.id, "scheduled")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#EBB04D] text-[#1C1917] px-4 py-2 text-xs font-bold hover:opacity-90"
                >
                  <Clock className="h-3.5 w-3.5" /> Mark scheduled
                </button>
              )}
              {r.status !== "completed" && (
                <button
                  onClick={() => updateStatus(r.id, "completed")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#9EA992] text-white px-4 py-2 text-xs font-bold hover:opacity-90"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark completed
                </button>
              )}
              {r.status !== "new" && (
                <button
                  onClick={() => updateStatus(r.id, "new")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#D6D3D1] text-[#1C1917] px-4 py-2 text-xs font-bold hover:bg-[#F0EFEB]"
                >
                  Reopen
                </button>
              )}
              <button
                onClick={() => remove(r.id)}
                className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#BD4C33] hover:text-[#D85C41] px-2"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Contact Messages Tab                                            */
/* -------------------------------------------------------------- */
function ContactsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/contacts");
      setItems(data);
    } catch (e) {
      toast.error("Couldn't load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await adminApi.delete(`/admin/contacts/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (loading)
    return (
      <div className="flex items-center gap-2 text-sm text-[#78716C]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Contact messages</h2>
        <span className="text-sm text-[#78716C]">{items.length} total</span>
      </div>
      {items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#D6D3D1] bg-[#F0EFEB] p-10 text-center text-sm text-[#78716C]">
          No messages yet.
        </div>
      )}
      {items.map((m, i) => (
        <div
          key={m.id}
          data-testid={`contact-msg-${i}`}
          className="rounded-2xl border border-[#E0DCD3] bg-white p-5 md:p-6"
        >
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-display text-lg font-bold">{m.name}</h3>
              <p className="text-sm">
                <a
                  href={`mailto:${m.email}`}
                  className="text-[#D85C41] hover:underline"
                >
                  {m.email}
                </a>
                {m.interest && (
                  <span className="ml-2 text-xs uppercase tracking-[0.12em] font-bold text-[#78716C]">
                    • {m.interest}
                  </span>
                )}
              </p>
              <p className="text-xs text-[#78716C] mt-1">
                {new Date(m.created_at).toLocaleString()}
              </p>
            </div>
            <button
              onClick={() => remove(m.id)}
              className="inline-flex items-center gap-1.5 text-xs text-[#BD4C33] hover:text-[#D85C41]"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
          <p className="mt-3 text-sm font-semibold text-[#1C1917]">
            {m.subject || "(no subject)"}
          </p>
          <p className="mt-1 text-sm text-[#57534E] whitespace-pre-wrap">
            {m.message}
          </p>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Dashboard Shell                                                 */
/* -------------------------------------------------------------- */
function Dashboard({ onLogout }) {
  const [site, setSite] = useState(null);

  const loadSite = async () => {
    const { data } = await axios.get(`${API}/site`);
    setSite(data);
  };

  useEffect(() => {
    loadSite();
  }, []);

  const saveSite = async (next) => {
    await adminApi.put("/admin/site", next);
    setSite(next);
  };

  if (!site)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F4] text-[#78716C]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading admin…
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <header className="border-b border-[#E7E2DA] bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#D85C41] text-white">
              <Heart className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <div>
              <p className="font-display text-base font-extrabold tracking-tight">
                Admin · CCDKOFHAMILTON
              </p>
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#78716C]">
                Manage your site
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-[#57534E] hover:text-[#D85C41]"
            >
              <ArrowLeft className="h-4 w-4" /> View site
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1C1917] text-white px-4 py-2 text-sm font-semibold hover:bg-[#D85C41]"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 md:px-10 py-10">
        <Tabs defaultValue="stats" className="w-full">
          <TabsList className="bg-white border border-[#E0DCD3] p-1.5 rounded-full flex flex-wrap gap-1 h-auto justify-start">
            <TabsTrigger value="stats" data-testid="tab-stats" className="rounded-full data-[state=active]:bg-[#1C1917] data-[state=active]:text-white px-4 py-2">
              <BarChart3 className="h-4 w-4 mr-1.5" /> Stats
            </TabsTrigger>
            <TabsTrigger value="stories" data-testid="tab-stories" className="rounded-full data-[state=active]:bg-[#1C1917] data-[state=active]:text-white px-4 py-2">
              <BookOpen className="h-4 w-4 mr-1.5" /> Stories
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos" className="rounded-full data-[state=active]:bg-[#1C1917] data-[state=active]:text-white px-4 py-2">
              <ImageIcon className="h-4 w-4 mr-1.5" /> Hero photos
            </TabsTrigger>
            <TabsTrigger value="info" data-testid="tab-info" className="rounded-full data-[state=active]:bg-[#1C1917] data-[state=active]:text-white px-4 py-2">
              <SettingsIcon className="h-4 w-4 mr-1.5" /> Site info
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-full data-[state=active]:bg-[#1C1917] data-[state=active]:text-white px-4 py-2">
              <Inbox className="h-4 w-4 mr-1.5" /> Help requests
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-full data-[state=active]:bg-[#1C1917] data-[state=active]:text-white px-4 py-2">
              <Mail className="h-4 w-4 mr-1.5" /> Messages
            </TabsTrigger>
          </TabsList>

          <div className="mt-8 rounded-3xl bg-[#F0EFEB] border border-[#E0DCD3] p-6 md:p-10">
            <TabsContent value="stats"><StatsTab site={site} onSave={saveSite} /></TabsContent>
            <TabsContent value="stories"><StoriesTab site={site} onSave={saveSite} /></TabsContent>
            <TabsContent value="photos"><PhotosTab site={site} onSave={saveSite} /></TabsContent>
            <TabsContent value="info"><SiteInfoTab site={site} onSave={saveSite} /></TabsContent>
            <TabsContent value="requests"><HelpRequestsTab /></TabsContent>
            <TabsContent value="contacts"><ContactsTab /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Page wrapper                                                    */
/* -------------------------------------------------------------- */
export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setChecking(false);
      return;
    }
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  if (checking)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F4] text-[#78716C]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Checking…
      </div>
    );

  if (!authed) return <LoginForm onLoggedIn={() => setAuthed(true)} />;

  return (
    <Dashboard
      onLogout={() => {
        clearToken();
        setAuthed(false);
      }}
    />
  );
}
